/* eslint-disable no-unused-vars */

import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useContext } from 'react';

import Burger from 'components/shared/burger';
import Button from 'components/shared/button';
import Link from 'components/shared/link';
import MainContext from 'context/main';
import i18nTranslations from 'i18n';
import logo from 'images/logo.svg';
import getFormattedId from 'utils/get-formatted-id';

import LangSelect from '../lang-select';

import DropdownMenu from './dropdown-menu';

const getHomeLink = (lang) => {
  if (lang === 'ca') return '/cat';
  if (lang === 'ja') return '/jp';

  return '/';
};

const Header = ({ theme, isMobileMenuOpen, onBurgerClick }) => {
  const {
    menus: { headerMenuItems: items, headerJPMenuItems: itemsJP },
    globalFields: {
      headerLink: { title: linkText, url: linkUrl },
      headerButton: { title: buttonText, url: buttonUrl },
    },
    translations,
    language,
  } = useContext(MainContext);

  const [activeDropdownLabel, setActiveDropdownLabel] = useState(null);

  const handleDropdownClick = (label) => () => setActiveDropdownLabel(label);

  const activeLocale = language?.locale ? language.locale : 'en';

  const menuItems = activeLocale === 'ja' ? itemsJP : items;

  const menuIds = useMemo(
    () =>
      items.map(({ label, childItems }) => ({
        id: getFormattedId(label),
        childItems: childItems.nodes.map(({ label, childItems }) => ({
          id: getFormattedId(label),
          childItems: childItems.nodes.map(({ label, childItems }) => ({
            id: getFormattedId(label),
            childItems: childItems.nodes.map(({ label }) => ({ id: getFormattedId(label) })),
          })),
        })),
      })),
    [items]
  );

  const homeUrl = getHomeLink(activeLocale);

  return (
    <header
      className={clsx('safe-paddings relative z-50 xl:pb-1', {
        'bg-white': theme === 'white',
        'bg-gray-1': theme === 'gray',
        '!bg-white': isMobileMenuOpen,
      })}
    >
      <div className="container relative flex h-24 items-center justify-between pb-8 xl:h-16 xl:p-3 xl:pl-5">
        <Link to={homeUrl} title="← Back to the homepage">
          <img className="h-10" src={logo} width={138} height={40} alt="RevenueCat" />
          <span className="sr-only">RevenueCat</span>
        </Link>
        <nav className="-ml-2.5 xl:hidden" onMouseLeave={handleDropdownClick(null)}>
          <ul className="flex gap-6 mt-1.5">
            {menuItems.map(({ label, path, childItems: { nodes: subItems } }, index) => {
              const itemLabel = label.toLowerCase();
              const hasChildItems = !!subItems?.length;
              const { id } = menuIds[index];

              return (
                <li
                  className={clsx(
                    activeDropdownLabel === itemLabel && activeDropdownLabel
                      ? 'before:opacity-100 after:opacity-100'
                      : 'before:opacity-0',
                    'relative px-0.5 before:absolute before:left-0 before:right-0 before:-bottom-[4.5px] before:z-50 before:mx-auto before:size-4 before:rotate-45 before:rounded-tl-[3px] before:border-l before:border-t before:border-gray-4 before:bg-white'
                  )}
                  key={label}
                >
                  <Link
                    className={clsx('mt-8 pb-[38px] after:!content-none', {
                      'pr-3 before:absolute before:-right-0.5 before:w-0 before:h-0 before:mt-1 before:border-4 before:border-[transparent] before:border-t-black before:!transition-[colors,transform] before:origin-[center_2px] before:!duration-200 before:hover:border-t-primary-2 group-hover:text-primary-2 group-hover:before:border-t-primary-2':
                        hasChildItems,
                      'text-primary-2 before:!right-0 before:rotate-180 before:border-t-primary-2':
                        activeDropdownLabel === itemLabel && activeDropdownLabel,
                    })}
                    to={path}
                    size="sm"
                    theme="black"
                    id={id}
                    onFocus={handleDropdownClick(hasChildItems ? itemLabel : null)}
                    onMouseOver={handleDropdownClick(hasChildItems ? itemLabel : null)}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>
          {menuItems
            .filter(({ childItems: { nodes: subItems } }) => !!subItems?.length)
            .map(({ label, childItems }, index) => {
              const dropdownLabel = label.toLowerCase();

              return (
                activeDropdownLabel === dropdownLabel && (
                  <DropdownMenu
                    key={label}
                    items={childItems.nodes}
                    ids={menuIds[index].childItems}
                    activeDropdownLabel={activeDropdownLabel}
                  />
                )
              );
            })}
        </nav>
        {translations && translations.length > 0 && (
          <LangSelect className="hidden ml-auto mr-8 xl:block xs:mr-2" openTo="bottom" />
        )}
        <div className="flex items-center space-x-8 xl:hidden">
          <Link
            className="!tracking-tight-4 !text-[14px]"
            to={linkUrl}
            size="md"
            theme="secondary-2"
          >
            {i18nTranslations[activeLocale][linkText]}
          </Link>
          <Button
            className="!h-[42px] px-7 !text-[14px] !tracking-tight-2"
            to={buttonUrl}
            size="sm"
            theme="secondary-2"
          >
            {i18nTranslations[activeLocale][buttonText]}
          </Button>
        </div>
        <Burger className="hidden xl:flex" isToggled={isMobileMenuOpen} onClick={onBurgerClick} />
      </div>
    </header>
  );
};

Header.propTypes = {
  theme: PropTypes.oneOf(['white', 'gray']).isRequired,
  isMobileMenuOpen: PropTypes.bool,
  onBurgerClick: PropTypes.func.isRequired,
};

Header.defaultProps = {
  isMobileMenuOpen: false,
};

export default Header;
